import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "@phosphor-icons/react";

const MODES = [
  ["standard", "Standard", "High confidence routes, uncertain confirms, low confidence asks. Best for most businesses."],
  ["cautious", "Cautious", "Requires stronger confidence before routing — more confirmations. Best for law, medical, insurance."],
  ["custom", "Custom", "You set the exact confidence needed to route without confirming."],
];

export default function PbxAiSettings({ orgId }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState("standard");
  const [threshold, setThreshold] = useState(0.75);

  const { data } = useQuery({
    queryKey: ["pbx-ai-config", orgId],
    queryFn: async () => (await api.get("/pbx/ai-config", { params: orgId ? { org_id: orgId } : {} })).data,
  });
  useEffect(() => { if (data) { setMode(data.mode); setThreshold(data.route_threshold ?? 0.75); } }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/pbx/ai-config", {
      mode, ...(mode === "custom" ? { route_threshold: threshold } : {}), ...(orgId ? { org_id: orgId } : {}),
    })).data,
    onSuccess: () => { toast.success("SI routing settings saved"); qc.invalidateQueries({ queryKey: ["pbx-ai-config"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <div className="max-w-2xl space-y-4" data-testid="pbx-ai-settings">
      <div className="flex items-center gap-2">
        <ShieldCheck size={20} className="text-primary" />
        <p className="text-sm text-muted-foreground">Control how confident the SI Receptionist must be before it transfers a caller.</p>
      </div>
      <div className="space-y-3">
        {MODES.map(([k, label, desc]) => (
          <label key={k} data-testid={`aimode-${k}`}
            className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors ${mode === k ? "border-primary bg-primary/5" : "border-white/10 bg-card hover:border-white/20"}`}>
            <input type="radio" name="aimode" checked={mode === k} onChange={() => setMode(k)} className="mt-1 h-4 w-4" />
            <div>
              <p className="text-sm font-semibold">{label}</p>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
          </label>
        ))}
      </div>
      {mode === "custom" && (
        <div className="rounded-2xl border border-white/10 bg-card p-4" data-testid="aimode-custom-slider">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Route without confirming when confidence ≥</span>
            <span className="font-mono-metric font-semibold text-primary">{Math.round(threshold * 100)}%</span>
          </div>
          <input type="range" min="0.3" max="0.99" step="0.01" value={threshold}
            data-testid="aimode-threshold" onChange={(e) => setThreshold(parseFloat(e.target.value))}
            className="mt-3 w-full accent-primary" />
          <p className="mt-1 text-xs text-muted-foreground">Below this, the SI confirms first; well below, it asks a clarifying question.</p>
        </div>
      )}
      <button data-testid="aiconfig-save" onClick={() => save.mutate()} disabled={save.isPending}
        className="rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
        {save.isPending ? "Saving…" : "Save settings"}
      </button>
      <p className="text-xs text-muted-foreground">Tip: you can force a confirmation on any individual intent (e.g. cancellations) from the SI Phrasebook tab.</p>
    </div>
  );
}
